import { useMemo, useSyncExternalStore } from "react"
import { config } from "@nova/configuration/browser"
import { selectActiveWorkspace } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import { OrganizationExtended } from "@nova/schemas"

type FeatureFlagKey = Parameters<typeof config.useFlag>[0]

export const useFeatureFlag = <K extends FeatureFlagKey>(flag: K): boolean | null => {
    const configFlag = config.useFlag(flag)

    const org = useAppSelector(selectActiveWorkspace())
    const orgFeatureFlags = useMemo(
        () => (org ? parseOrgFeatureFlags(org) : new Set<string>()),
        [org]
    )
    if (orgFeatureFlags.has(flag)) return true

    return configFlag
}

export const isFeatureFlagEnabled = (
    flag: FeatureFlagKey,
    org: OrganizationExtended | undefined
): boolean | null => {
    if (org && parseOrgFeatureFlags(org).has(flag)) return true
    return config.getFlag(flag)
}

const parseOrgFeatureFlags = (org: OrganizationExtended) => {
    const featureFlags = new Set<string>()
    const rawFeatureFlags = org.settings.find((setting) => setting.name === "featureFlags")?.value
    if (typeof rawFeatureFlags !== "string") return featureFlags

    try {
        const flags = JSON.parse(rawFeatureFlags)
        if (!Array.isArray(flags)) return featureFlags
        flags
            .filter((flag): flag is string => typeof flag === "string")
            .forEach((flag) => featureFlags.add(flag))
    } catch {}

    return featureFlags
}

export const featureFlagTogglesFlag = "flags"

const subscribeToFlags = (onChange: () => void) => config.subscribeToFlags(onChange)

export const useFeatureFlagToggles = () => {
    const enabledFlags = useSyncExternalStore(
        subscribeToFlags,
        () => config.getEnabledFlagsKey(),
        () => ""
    )

    return useMemo(
        () =>
            config.getFlags().map((flag) => ({
                flag,
                enabled: enabledFlags.split(",").includes(flag),
                setEnabled: (enabled: boolean) => config.setFlag(flag, enabled),
            })),
        [enabledFlags]
    )
}
